<template>
  <div class="p-6 max-w-full">
    <div class="mb-6 flex items-start justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Notas de Venta</h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm">POS2 — Documentos de venta</p>
      </div>
      <button
        @click="probarConectia"
        :disabled="probandoConectia"
        class="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition disabled:opacity-60 flex items-center gap-2"
        title="Envía un CFDI 4.0 de prueba al sandbox de SmarterWeb con datos hardcodeados"
      >
        <span>🧪</span>
        {{ probandoConectia ? 'Timbrando...' : 'Probar Facturación (SW Sandbox)' }}
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 flex flex-wrap gap-3 items-end">
      <div class="flex-1 min-w-48">
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Buscar</label>
        <input
          v-model="filtros.buscar"
          @keyup.enter="cargar(1)"
          type="text"
          placeholder="Folio nota, folio pedido..."
          class="w-full px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
        />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Estado</label>
        <select v-model="filtros.estado" @change="cargar(1)" class="px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
          <option value="">Todos</option>
          <option value="activa">Activa</option>
          <option value="facturada">Facturada</option>
          <option value="cancelada">Cancelada</option>
        </select>
      </div>
      <button @click="cargar(1)" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
      <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-300 transition">Limpiar</button>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div v-if="cargando" class="py-16 text-center text-gray-400">Cargando...</div>
      <div v-else-if="notas.length === 0" class="py-16 text-center text-gray-400">Sin notas de venta</div>
      <table v-else class="w-full text-sm text-left">
        <thead class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">
          <tr>
            <th class="px-4 py-3">Folio Nota</th>
            <th class="px-4 py-3">Pedido Origen</th>
            <th class="px-4 py-3">N° Orden</th>
            <th class="px-4 py-3">Cliente</th>
            <th class="px-4 py-3">Total</th>
            <th class="px-4 py-3">Estado</th>
            <th class="px-4 py-3">Creada</th>
            <th class="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="nota in notas"
            :key="nota.id"
            class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            <td class="px-4 py-3 font-mono font-semibold text-violet-700 dark:text-violet-400">{{ nota.folio }}</td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400">{{ nota.pedido?.folio ?? '—' }}</td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400">{{ nota.orden_produccion?.numero_orden ?? '—' }}</td>
            <td class="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{{ nota.snapshot_header?.cliente ?? '—' }}</td>
            <td class="px-4 py-3 font-semibold text-gray-800 dark:text-gray-200">{{ formatMoney(nota.snapshot_header?.total_venta) }}</td>
            <td class="px-4 py-3">
              <span :class="badgeEstado(nota.estado)" class="text-xs font-semibold px-2 py-0.5 rounded-full">
                {{ labelEstado(nota.estado) }}
              </span>
            </td>
            <td class="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{{ formatFecha(nota.created_at) }}</td>
            <td class="px-4 py-3">
              <div class="flex gap-2 justify-end">
                <button
                  @click="verNota(nota)"
                  class="px-2.5 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-violet-100 hover:text-violet-700 transition text-xs font-medium"
                >
                  Ver
                </button>
                <button
                  v-if="nota.estado === 'activa'"
                  @click="cambiarEstado(nota, 'cancelada')"
                  class="px-2.5 py-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200 transition text-xs font-medium"
                >
                  Cancelar
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="meta.last_page > 1" class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
      <span>Página {{ meta.current_page }} de {{ meta.last_page }} — {{ meta.total }} resultados</span>
      <div class="flex gap-2">
        <button @click="cargar(meta.current_page - 1)" :disabled="meta.current_page <= 1"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Anterior</button>
        <button @click="cargar(meta.current_page + 1)" :disabled="meta.current_page >= meta.last_page"
          class="px-3 py-1 rounded border dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Siguiente</button>
      </div>
    </div>

    <!-- Modal nota -->
    <ModalNota
      :visible="modalNota.visible"
      :nota="modalNota.nota"
      @cerrar="modalNota.visible = false"
      @facturada="onNotaFacturada"
    />

    <!-- Modal Resultado Prueba Conectia/SW -->
    <div v-if="modalPrueba.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style="max-height: 92vh;">
        <div class="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 shrink-0">
          <div class="flex items-center gap-3">
            <h3 class="text-base font-bold text-gray-900 dark:text-white">Resultado Prueba — SmarterWeb Sandbox</h3>
            <span
              :class="modalPrueba.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
              class="text-xs font-semibold px-2 py-0.5 rounded-full"
            >
              {{ modalPrueba.ok ? 'OK' : 'ERROR' }}
            </span>
            <span v-if="modalPrueba.httpStatus" class="text-xs font-mono text-gray-500">HTTP {{ modalPrueba.httpStatus }}</span>
          </div>
          <button @click="modalPrueba.visible = false" class="text-gray-400 hover:text-gray-600 p-1">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          <div v-if="modalPrueba.message" class="px-3 py-2 rounded bg-amber-50 text-amber-800 border border-amber-200">
            {{ modalPrueba.message }}
          </div>

          <div v-if="modalPrueba.requestUrl">
            <p class="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-1">Endpoint</p>
            <code class="block px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono break-all">{{ modalPrueba.requestUrl }}</code>
          </div>

          <div v-if="modalPrueba.requestPayload">
            <p class="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-1">Request Body</p>
            <pre class="px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono overflow-x-auto max-h-64 text-gray-800 dark:text-gray-200">{{ JSON.stringify(modalPrueba.requestPayload, null, 2) }}</pre>
          </div>

          <div v-if="modalPrueba.responseBody || modalPrueba.responseRaw">
            <p class="text-xs font-bold uppercase text-gray-500 dark:text-gray-400 mb-1">Response</p>
            <pre class="px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono overflow-x-auto max-h-96 text-gray-800 dark:text-gray-200">{{ modalPrueba.responseBody ? JSON.stringify(modalPrueba.responseBody, null, 2) : modalPrueba.responseRaw }}</pre>
          </div>
        </div>

        <div class="flex justify-end gap-2 px-6 py-4 border-t dark:border-gray-700">
          <button @click="modalPrueba.visible = false" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 transition">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import ModalNota from './ModalNota.vue'

const http      = useHttp()
const httpPatch = useHttp()

const notas    = ref([])
const cargando = ref(false)
const meta     = ref({ current_page: 1, last_page: 1, total: 0 })
const filtros  = ref({ buscar: '', estado: '' })

const modalNota = ref({ visible: false, nota: {} })

const probandoConectia = ref(false)
const modalPrueba = ref({
  visible: false,
  ok: false,
  httpStatus: null,
  message: '',
  requestUrl: '',
  requestPayload: null,
  responseBody: null,
  responseRaw: '',
})

const probarConectia = async () => {
  probandoConectia.value = true
  const httpPrueba = useHttp()
  try {
    await httpPrueba.post('/pos2/conectia/test', {})
    const r = httpPrueba.data.value ?? {}
    modalPrueba.value = {
      visible: true,
      ok: !!r.ok,
      httpStatus: r.http_status ?? null,
      message: r.message ?? '',
      requestUrl: r.request_url ?? '',
      requestPayload: r.request_payload ?? null,
      responseBody: r.response_body ?? null,
      responseRaw: r.response_raw ?? '',
    }
  } catch (err) {
    const r = err.response?.data ?? {}
    modalPrueba.value = {
      visible: true,
      ok: false,
      httpStatus: r.http_status ?? err.response?.status ?? null,
      message: r.message ?? err.message ?? 'Error desconocido',
      requestUrl: r.request_url ?? '',
      requestPayload: r.request_payload ?? null,
      responseBody: r.response_body ?? null,
      responseRaw: r.response_raw ?? '',
    }
  } finally {
    probandoConectia.value = false
  }
}

const cargar = async (pagina = 1) => {
  cargando.value = true
  try {
    const params = new URLSearchParams({ page: pagina, per_page: 15 })
    if (filtros.value.buscar) params.set('buscar', filtros.value.buscar)
    if (filtros.value.estado) params.set('estado', filtros.value.estado)

    await http.get(`/pos2/notas?${params}`)
    const res = http.data.value
    notas.value = res.data ?? []
    meta.value  = { current_page: res.current_page, last_page: res.last_page, total: res.total }
  } finally {
    cargando.value = false
  }
}

const limpiar = () => {
  filtros.value = { buscar: '', estado: '' }
  cargar(1)
}

const verNota = (nota) => {
  modalNota.value = { visible: true, nota }
}

const onNotaFacturada = (notaId) => {
  const idx = notas.value.findIndex(n => n.id === notaId)
  if (idx !== -1) notas.value[idx] = { ...notas.value[idx], estado: 'facturada' }
  if (modalNota.value.nota?.id === notaId) {
    modalNota.value.nota = { ...modalNota.value.nota, estado: 'facturada' }
  }
}

const cambiarEstado = async (nota, estado) => {
  try {
    await httpPatch.patch(`/pos2/notas/${nota.id}/estado`, { estado })
    const idx = notas.value.findIndex(n => n.id === nota.id)
    if (idx !== -1) notas.value[idx] = { ...notas.value[idx], estado }
  } catch (err) {
    alert(err.response?.data?.message ?? 'Error al cambiar estado')
  }
}

const formatFecha = (val) => {
  if (!val) return '—'
  return new Date(val).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
}

const formatMoney = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val ?? 0)

const labelEstado = (e) => ({ activa: 'Activa', facturada: 'Facturada', cancelada: 'Cancelada' }[e] ?? e)

const badgeEstado = (e) => ({
  activa:    'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  facturada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelada: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
}[e] ?? 'bg-gray-100 text-gray-600')

onMounted(() => cargar(1))
</script>
